// 二角取り Solitaire **固有**の設定（localStorage 永続）。
//
// ポータル横断の設定（音量・ミュート・BGM）は [settings.ts](settings.ts) が持つ。
// ここにあるのは「このゲームでしか意味を持たない」項目だけ:
//   - effectSpeed: エフェクト速度 0..1（0=Slow 遅い / 1=Fast 速い）。牌の裏返しアニメの再生速度。
//   - routeLineOn: 道順ライン描画 ON/OFF（GameScene の経路線 drawConnection）。
//
// キーは `nikakudori.*`（＝ゲーム名）。プレイヤー識別や音量のような
// **ポータル全体で1つ**の値は `hongsoft.*` を使う。この使い分けが所属の唯一の表明。
// ※ 投影モード / 言語設定 はオリジナルにあるが Web版では非採用（保留）。
import { GAME_KEY } from '@/games/nikakudori/gameKey';
import type { GameSettingsSlice } from '@/platform/net/settings';

const K_EFFECT = 'nikakudori.effectSpeed';
const K_ROUTE = 'nikakudori.routeLineOn';

export const GAME_SETTINGS_DEFAULTS = {
  effectSpeed: 0.6,
  routeLineOn: true,
} as const;

// エフェクト速度 0..1 → 裏返しアニメの1フレーム表示時間(ms)。
// 0=Slow(遅い=長い) / 1=Fast(速い=短い)。既定 0.6 で約80ms（従来値）。
const EFFECT_MS_SLOW = 150;
const EFFECT_MS_FAST = 35;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/** エフェクト速度 0..1（0=Slow 遅い / 1=Fast 速い）。既定 0.6。 */
export function getEffectSpeed(): number {
  const v = localStorage.getItem(K_EFFECT);
  if (v === null) return GAME_SETTINGS_DEFAULTS.effectSpeed;
  const n = Number(v);
  return Number.isFinite(n) ? clamp01(n) : GAME_SETTINGS_DEFAULTS.effectSpeed;
}
export function setEffectSpeed(v: number): void {
  localStorage.setItem(K_EFFECT, String(clamp01(v)));
}

/** 現在のエフェクト速度に対応する、裏返しアニメの1フレーム表示時間(ms)。 */
export function effectFrameMs(): number {
  return Math.round(EFFECT_MS_SLOW + (EFFECT_MS_FAST - EFFECT_MS_SLOW) * getEffectSpeed());
}

/** 道順ライン（経路線）を描画するか（既定: ON）。 */
export function isRouteLineOn(): boolean {
  const v = localStorage.getItem(K_ROUTE);
  return v === null ? GAME_SETTINGS_DEFAULTS.routeLineOn : v === '1';
}
export function setRouteLineOn(on: boolean): void {
  localStorage.setItem(K_ROUTE, on ? '1' : '0');
}

/** このゲームの設定を既定値へ戻す（PREFERENCE の「初期設定に戻す」の一部）。 */
export function resetGameSettings(): void {
  setEffectSpeed(GAME_SETTINGS_DEFAULTS.effectSpeed);
  setRouteLineOn(GAME_SETTINGS_DEFAULTS.routeLineOn);
}

/**
 * サーバー同期用のスライス。settings.ts の `GAME_SETTINGS_SLICES` に載せると、
 * `/api/settings` の `games.<gameKey>` として自動で往復するようになる。
 * ゲームを増やすときは、そのゲームの設定モジュールで同じ形を輸出して配列へ足すだけでよい。
 */
export const nikakudoriSettingsSlice: GameSettingsSlice = {
  gameKey: GAME_KEY,
  read: () => ({
    effectSpeed: getEffectSpeed(),
    routeLineOn: isRouteLineOn(),
  }),
  apply: (s) => {
    if (typeof s.effectSpeed === 'number') setEffectSpeed(s.effectSpeed);
    if (typeof s.routeLineOn === 'boolean') setRouteLineOn(s.routeLineOn);
  },
  reset: resetGameSettings,
};
